import React from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { useShellAppContext } from '../../shell/context/useShellAppContext';
import { Links } from '../../../containers/AppLayout/menu/linkBuilder';
import { useAgentMcpStatus } from '../../../pluginAdmin/features/agentMcp/useAgentMcpStatus';
import styles from './AgentMcpSetupCard.module.scss';

function SparklesIcon() {
  return (
    <svg
      className={styles.sparklesIcon}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      data-testid="agent-mcp-ai-icon"
    >
      <path d="M11.5 2.5c.45 3.66 2.34 5.55 6 6-3.66.45-5.55 2.34-6 6-.45-3.66-2.34-5.55-6-6 3.66-.45 5.55-2.34 6-6Z" />
      <path d="M18.5 14.5c.22 1.83 1.17 2.78 3 3-1.83.22-2.78 1.17-3 3-.22-1.83-1.17-2.78-3-3 1.83-.22 2.78-1.17 3-3Z" />
      <path d="M5 2c.14 1.14.86 1.86 2 2-1.14.14-1.86.86-2 2-.14-1.14-.86-1.86-2-2 1.14-.14 1.86-.86 2-2Z" />
    </svg>
  );
}

export default function AgentMcpSetupCard({ variant }) {
  const t = useLocalizedMessage();
  const history = useHistory();
  const { capabilities, bridgeClient, routeShopId } = useShellAppContext();
  const supported = capabilities?.agentMcpAdminV1 === true;
  const { state, isLoading, error } = useAgentMcpStatus({ enabled: supported, bridgeClient });

  if (!supported) return null;

  const statusKey = error
    ? 'agentMcp.home.status.unavailable'
    : state?.ready
      ? 'agentMcp.home.status.ready'
      : state?.configured
        ? 'agentMcp.home.status.disabled'
        : 'agentMcp.home.status.setup';
  const isSetupVariant = variant === 'setup';

  return (
    <section
      className={isSetupVariant ? styles.setupCard : styles.statusCard}
      data-testid={isSetupVariant ? 'agent-mcp-setup-cta' : 'agent-mcp-home-card'}
    >
      <div className={`${styles.icon} ${error ? styles.iconMuted : ''}`}>
        {isLoading ? <CircularProgress size={18} color="inherit" /> : <SparklesIcon />}
      </div>
      <div className={styles.content}>
        <p className={isSetupVariant ? styles.setupTitle : styles.statusTitle}>
          {t(isSetupVariant ? 'agentMcp.home.setup.title' : 'agentMcp.home.status.title')}
        </p>
        {isSetupVariant ? (
          <p className={styles.setupDescription}>{t('agentMcp.home.setup.description')}</p>
        ) : (
          <p className={styles.statusValue}>{t(statusKey)}</p>
        )}
      </div>
      <div className={styles.action}>
        <Button
          variant={isSetupVariant || !state?.ready ? 'contained' : 'outlined'}
          color="primary"
          size={isSetupVariant ? 'medium' : 'small'}
          onClick={() => history.push(Links.searchAgentMcpSettings(routeShopId))}
        >
          {state?.ready && !isSetupVariant
            ? t('agentMcp.home.manage')
            : t('agentMcp.home.configure')}
        </Button>
      </div>
    </section>
  );
}

AgentMcpSetupCard.propTypes = {
  variant: PropTypes.oneOf(['setup', 'status']),
};

AgentMcpSetupCard.defaultProps = {
  variant: 'status',
};
